import { platformService, trackingService } from '@monthio/react-components'
import { captureException } from '@sentry/react'
import { call, put } from 'typed-redux-saga'

import type {
	AuthenticateFailedAction,
	AuthenticateSucceededAction,
	AuthenticateWithApplicantIdAction,
} from '../actions/authenticateActions'
import type { GoToFlowAction } from '../actions/flowEntryStatemachineActions'

import { budgetApiClient } from '../../api/budgetApiClient'
import { compareAuthenticationResponses } from '../../utils/compareAuthenticationResponses'
import { signalRMessaging } from '../../services/realtimeCommunicationService/signalRMessaging'
import { redirectService } from '../../services/redirectService'
import {
	authenticateFailed,
	authenticateSucceeded,
	authenticateWithApplicantId,
	reAuthenticate,
} from '../actions/authenticateActions'

export function* goToFlowAuthenticateSaga(
	action: AuthenticateWithApplicantIdAction,
): Generator<any, void, GoToFlowAction | any> {
	const { applicantId } = action.payload
	yield put(authenticateWithApplicantId(applicantId))
}

export function* authenticateWithApplicantIdSaga(
	action: AuthenticateWithApplicantIdAction,
): Generator<
	any,
	void,
	AuthenticateSucceededAction | AuthenticateFailedAction | any
> {
	const { applicantId } = action.payload
	try {
		redirectService.setApplicantId(applicantId)

		const [smartCheckAuthenticationDto, adaResponse] = yield* call(() =>
			Promise.all([
				budgetApiClient.authenticateSessionWithApplicant(applicantId),
				budgetApiClient
					.authenticateSessionWithApplicantV2(applicantId)
					.catch(() => null),
			]),
		)

		platformService.setAccessToken(smartCheckAuthenticationDto.token)

		const effectiveDto =
			adaResponse != null
				? {
						...smartCheckAuthenticationDto,
						smartCheckConfiguration: {
							...smartCheckAuthenticationDto.smartCheckConfiguration,
							shouldInputCooperativeHomeValue:
								adaResponse.smartCheckConfiguration
									.shouldInputCooperativeHomeValue,
						},
					}
				: smartCheckAuthenticationDto

		yield put(authenticateSucceeded(applicantId, effectiveDto))
		yield put(reAuthenticate(applicantId, effectiveDto.token))
		yield signalRMessaging.initialize({
			accessToken: smartCheckAuthenticationDto.token,
			hubUrl: `${platformService.getAppUrl(
				'budgetApp',
			)}/hubs/anonymous-consumer`,
		})

		try {
			redirectService.setRedirectUrl({
				applicantId,
				redirectUrl:
					smartCheckAuthenticationDto.smartCheckConfiguration.redirectOptions
						.url,
				consumerId: smartCheckAuthenticationDto.consumerId,
			})
		} catch (error) {
			captureException(error)
		}

		try {
			if (adaResponse == null) {
				trackingService.trackTechnicalEvent('AuthSessionV2Comparison', {
					v2Available: false,
				})
			} else {
				const result = compareAuthenticationResponses(
					smartCheckAuthenticationDto,
					adaResponse,
				)
				const differencesJson = JSON.stringify(result.differences)
				const truncated = differencesJson.length > 5000
				trackingService.trackTechnicalEvent('AuthSessionV2Comparison', {
					v2Available: true,
					isMatch: result.isMatch,
					differenceCount: result.differenceCount,
					differences: truncated
						? differencesJson.substring(0, 5000)
						: differencesJson,
					truncated,
				})
			}
		} catch (error) {
			captureException(error)
		}
	} catch (error: any) {
		yield put(
			authenticateFailed({
				errorCode: error.errorCode,
				errorText: error.name + ': ' + error.message,
				innerErrors: [],
			}),
		)
	}
}
